import axios from '@/modules/axios/index';
import { Controller } from "@hotwired/stimulus";

/**
 * 収穫金変換モーダルコントローラー
 *
 * 責任範囲:
 * - 収穫金→ポイント変換モーダルの表示・非表示制御
 * - 変換額の自動計算・設定
 * - 変換後の残高プレビュー表示
 * - 変換API実行
 *
 * 使用シナリオ:
 * - シナリオ(2): ポイント不足、収穫金あり
 */
export class ConvertPointsModalController extends Controller {
  static targets = [
    'modal',
    'convertPointsField',
    'beforeConvertHarvests',
    'beforeConvertPoints',
    'afterConvertedHarvests',
    'afterConvertedPoints',
    'errorBlock',
    'insufficientText',
    'additionalNote',
    'harvestShortageAmount'
  ]

  static values = {
    availablePoints: Number,
    availableHarvests: Number
  }

  /**
   * 初期化
   */
  connect() {
    this.convertPointsFieldTarget.addEventListener(
      "input", this.updatePreview.bind(this));
  }

  /**
   * 破棄
   */
  disconnect() {
    this.convertPointsFieldTarget.removeEventListener(
      "input", this.updatePreview.bind(this));
  }

  /**
   * モーダルを表示して変換額を自動設定
   * @param {Object} data - 表示データ
   * @param {number} data.insufficientPoints - 不足ポイント額
   */
  show(data) {
    // 説明文の不足額を更新
    this.insufficientTextTarget.textContent =
      'MFJPY ¥' + this.formatCurrency(data.insufficientPoints);

    // 追加メッセージの表示制御（収穫金残高が不足している場合に表示）
    if (this.availableHarvestsValue < data.insufficientPoints) {
      // 収穫金の不足額を計算
      const shortageAmount = data.insufficientPoints - this.availableHarvestsValue;
      this.harvestShortageAmountTarget.textContent = this.formatCurrency(shortageAmount);
      this.additionalNoteTarget.style.display = 'block';
    } else {
      this.additionalNoteTarget.style.display = 'none';
    }

    // 「振替前」の残高表示を最新値に更新
    this.beforeConvertHarvestsTarget.textContent = this.formatCurrency(this.availableHarvestsValue);
    this.beforeConvertPointsTarget.textContent = this.formatCurrency(this.availablePointsValue);

    // 不足ポイント分を自動計算して変換額フィールドに設定
    const convertAmount = Math.min(data.insufficientPoints, this.availableHarvestsValue);
    if (convertAmount > 0) {
      this.convertPointsFieldTarget.value = convertAmount;
      this.updatePreview();
    }

    // モーダルを表示
    this.modalTarget.style.display = 'block';
    this.modalTarget.style.opacity = 1;
  }

  /**
   * 変換額入力時のプレビュー更新
   * 概要: 「この金額を変換すると残高がいくらになる？」を即座にプレビュー表示
   * トリガー: 収穫金変換額入力フィールドのinputイベント
   */
  updatePreview() {
    const points = Number(this.convertPointsFieldTarget.value);
    if (points === '') { return }

    // 変換後の収穫金残高を計算（現在の収穫金 - 変換額）
    const afterHarvests = Number(this.availableHarvestsValue) - points;
    if (afterHarvests < 0) { return }  // マイナスになる場合は処理しない

    // 変換後の収穫金残高を表示更新（例: ¥50,000）
    this.afterConvertedHarvestsTarget.textContent = this.formatCurrency(afterHarvests);
    // 変換後のMFJPY残高を表示更新（例: ¥150,000）
    this.afterConvertedPointsTarget.textContent = this.formatCurrency(Number(this.availablePointsValue) + points);
  }

  /**
   * 収穫金のポイント変換Submit
   * トリガー: 収穫金変換フォームのsubmitイベント
   * HTML: _convert_to_point_modal.html.erb:12
   *       data-action="submit->convert-points-modal#submit"
   */
  submit(event) {
    event.preventDefault();
    if (this.convertPointsFieldTarget.value === '') {
      return;
    }

    this.convertToPoints({ amount: this.convertPointsFieldTarget.value });
  }

  /**
   * 収穫金のポイント変換API実行
   * @param {Object} params - リクエストパラメータ
   * @param {number} params.amount - 変換金額
   */
  convertToPoints(params) {
    this.hideError();

    axios.post('/api/v1/customer/harvests/convert', params)
      .then((response) => {
        // 残高を更新
        this.availablePointsValue = response.data.available_points;
        this.availableHarvestsValue = response.data.available_harvests;

        // 即座に成功画面を表示
        this.modalTarget.dataset.screen = 'success';
      })
      .catch(({response}) => {
        this.showError(response.data.message);
      });
  }

  /**
   * 成功画面から次の処理へ進む
   * トリガー: 成功画面の「確認」ボタンclickイベント
   * HTML: _convert_to_point_modal.html.erb:117
   *       data-action="convert-points-modal#closeAndProceed"
   * 動作: モーダルを閉じて、次のモーダル表示のためにイベントを発火
   */
  closeAndProceed(event) {
    event.preventDefault();

    // モーダルを閉じる前にdata-screenをpurchaseに戻す（次回表示時のため）
    this.modalTarget.dataset.screen = 'purchase';

    // モーダルを閉じる
    this.close();

    // 親コントローラーに変換成功を通知（次のモーダルを表示）
    this.dispatch('converted', {
      detail: {
        availablePoints: this.availablePointsValue,
        availableHarvests: this.availableHarvestsValue
      }
    });
  }

  /**
   * モーダルを閉じる
   * トリガー: 「戻る」ボタンclickイベント
   * HTML: _convert_to_point_modal.html.erb:78
   *       data-action="convert-points-modal#close"
   */
  close(event) {
    if (event) {
      event.preventDefault();
    }
    this.modalTarget.style.display = 'none';
    this.modalTarget.style.opacity = 0;
  }

  /**
   * エラーメッセージを表示
   * @param {string} message - エラーメッセージ
   */
  showError(message) {
    this.errorBlockTarget.style.display = 'block';
    this.errorBlockTarget.textContent = message;
  }

  /**
   * エラーメッセージを非表示
   */
  hideError() {
    this.errorBlockTarget.style.display = 'none';
    this.errorBlockTarget.textContent = '';
  }

  /**
   * 数値をカンマ区切りにフォーマット
   * @param {number} value - フォーマットする数値
   * @returns {string} カンマ区切りの文字列
   */
  formatCurrency(value) {
    return value.toLocaleString('en-US');
  }
}
